/**
 * Saying why a write did not stick.
 *
 * Firestore applies a write locally before the server has seen it, so a
 * rejected one is visible for a moment and then gone: the screen moves on, then
 * moves back. Without a message that reads as a *broken app* rather than as a
 * refusal, and the most common refusal — rules that have not been deployed yet
 * — is invisible from the browser and obvious from one sentence.
 *
 * Pure and exported so it can be tested without Firestore.
 */

export function writeErrorCode(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

export function describeWriteError(err: unknown): string {
  switch (writeErrorCode(err)) {
    case 'permission-denied':
      return 'The server refused that change. If this has just been deployed, '
        + 'the security rules may be older than the app: deploy them with '
        + '`firebase deploy --only firestore:rules`.';
    case 'unavailable':
      return 'Could not reach the database. Check the connection and try again.';
    case 'unauthenticated':
      return 'Your session has expired. Sign out and back in.';
    case 'not-found':
      return 'The record being changed does not exist. It may need to be provisioned first.';
    case null:
      return 'That could not be saved. Please try again.';
    default:
      return `That could not be saved (${writeErrorCode(err)}).`;
  }
}
